import { useEffect, useMemo, useState } from "react";

const IconMorning = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
  </svg>
);

const IconAfternoon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 3a9 9 0 0 0 0 18z" fill="currentColor" stroke="none" />
  </svg>
);

const IconNight = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />
  </svg>
);

/* Minutos desde medianoche. Acepta "14:30" y "2:30 PM" */
const getSlotMinutes = (value) => {
  const match = String(value ?? "").trim().match(/(\d{1,2})(?::(\d{2}))?\s*(am|pm|a\.m\.|p\.m\.)?/i);
  if (!match) return null;

  let hour = Number(match[1]);
  const minutes = Number(match[2] || 0);
  const meridiem = (match[3] || "").toLowerCase().replace(/\./g, "");

  if (meridiem === "pm" && hour < 12) hour += 12;
  if (meridiem === "am" && hour === 12) hour = 0;

  return hour * 60 + minutes;
};

const getSlotHour = (value) => {
  const minutes = getSlotMinutes(value);
  return minutes === null ? null : Math.floor(minutes / 60);
};

/* Compara solo día/mes/año en hora local, sin convertir a UTC */
const isToday = (date) => {
  if (!date) return false;
  const now = new Date();

  if (typeof date === "string") {
    const parts = date.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (parts) {
      return (
        Number(parts[1]) === now.getFullYear() &&
        Number(parts[2]) === now.getMonth() + 1 &&
        Number(parts[3]) === now.getDate()
      );
    }
  }

  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return false;

  return (
    parsed.getFullYear() === now.getFullYear() &&
    parsed.getMonth() === now.getMonth() &&
    parsed.getDate() === now.getDate()
  );
};

const PERIODS = [
  { id: "morning",   label: "Mañana", Icon: IconMorning,   match: (h) => h < 12 },
  { id: "afternoon", label: "Tarde",  Icon: IconAfternoon, match: (h) => h >= 12 && h < 18 },
  { id: "night",     label: "Noche",  Icon: IconNight,     match: (h) => h >= 18 }
];

export default function TimePicker({
  slots = [],
  selectedSlot,
  onSelect,
  duration,
  date,                  // fecha elegida: Date o "YYYY-MM-DD"
  leadTimeMinutes = 0    // margen mínimo de anticipación
}) {
  const hasSlots = Array.isArray(slots) && slots.length > 0;
  const todaySelected = isToday(date);

  /* Reloj en minutos, refrescado cada 60s para que los horarios
     se vayan cayendo solos mientras el cliente está en la pantalla */
  const [nowMinutes, setNowMinutes] = useState(() => {
    const now = new Date();
    return now.getHours() * 60 + now.getMinutes();
  });

  useEffect(() => {
    if (!todaySelected) return;

    const id = setInterval(() => {
      const now = new Date();
      setNowMinutes(now.getHours() * 60 + now.getMinutes());
    }, 60000);

    return () => clearInterval(id);
  }, [todaySelected]);

  const groups = useMemo(() => {
    const base = { morning: [], afternoon: [], night: [] };
    if (!hasSlots) return base;

    slots.forEach((slot) => {
      const minutes = getSlotMinutes(slot.from);

      // Solo se ocultan los que ya pasaron; los ocupados siguen visibles
      const alreadyPassed =
        todaySelected &&
        minutes !== null &&
        minutes <= nowMinutes + leadTimeMinutes;

      if (alreadyPassed) return;

      const hour = minutes === null ? null : Math.floor(minutes / 60);
      const period = PERIODS.find((p) => hour !== null && p.match(hour)) || PERIODS[1];
      base[period.id].push(slot);
    });

    return base;
  }, [slots, hasSlots, todaySelected, nowMinutes, leadTimeMinutes]);

  const counts = useMemo(
    () => ({
      morning: groups.morning.filter((s) => !s.disabled).length,
      afternoon: groups.afternoon.filter((s) => !s.disabled).length,
      night: groups.night.filter((s) => !s.disabled).length
    }),
    [groups]
  );

  const totalVisible =
    groups.morning.length + groups.afternoon.length + groups.night.length;

  const [activePeriod, setActivePeriod] = useState("morning");

  useEffect(() => {
    if (!hasSlots) return;

    if (selectedSlot?.from) {
      const hour = getSlotHour(selectedSlot.from);
      const period = PERIODS.find((p) => hour !== null && p.match(hour));
      if (period && groups[period.id].length) {
        setActivePeriod(period.id);
        return;
      }
    }

    const firstWithFree = PERIODS.find((p) => counts[p.id] > 0);
    const firstWithAny = PERIODS.find((p) => groups[p.id].length > 0);
    setActivePeriod((firstWithFree || firstWithAny || PERIODS[0]).id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slots, date]);

  if (!hasSlots) {
    return (
      <div className="ck-empty">
        No hay horarios para esta fecha.
      </div>
    );
  }

  if (totalVisible === 0) {
    return (
      <div className="ck-empty">
        Ya no quedan horarios disponibles para hoy. Prueba con otra fecha.
      </div>
    );
  }

  const activeConfig = PERIODS.find((p) => p.id === activePeriod) || PERIODS[0];
  const visibleSlots = groups[activePeriod] || [];

  return (
    <div className="ck-time">
      <div className="ck-time-tabs" role="tablist" aria-label="Franja horaria">
        {PERIODS.map(({ id, label, Icon }) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={activePeriod === id}
            className={`ck-time-tab${activePeriod === id ? " active" : ""}`}
            onClick={() => setActivePeriod(id)}
          >
            <Icon />
            <span className="ck-time-tab-label">{label}</span>
            <span className="ck-time-tab-count">{counts[id]}</span>
          </button>
        ))}
      </div>

      <div className="ck-time-meta">
        <p className="ck-time-meta-title">
          <span className="ck-time-meta-num">{counts[activePeriod]}</span>
          {" Horarios disponibles: "}
          {activeConfig.label}
        </p>
        {duration ? <span className="ck-time-meta-note">Duración {duration}</span> : null}
      </div>

      {visibleSlots.length ? (
        <div className="ck-time-grid">
          {visibleSlots.map((slot, index) => {
            const selected =
              selectedSlot?.from === slot.from && selectedSlot?.to === slot.to;

            return (
              <button
                key={`${slot.from}-${slot.to}-${index}`}
                type="button"
                className={`ck-time-slot${slot.disabled ? " unavailable" : ""}${selected ? " selected" : ""}`}
                onClick={() => !slot.disabled && onSelect({ from: slot.from, to: slot.to })}
                disabled={slot.disabled}
              >
                {slot.from} - {slot.to}
              </button>
            );
          })}
        </div>
      ) : (
        <div className="ck-time-none">
          No hay horarios en esta franja.
        </div>
      )}
    </div>
  );
}